import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Stack,
  Grid2,
  Chip,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from "../Header";
import Footer from "../Footer";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fetchReceivedPaymentcollectionList } from "../../services/AccountServices";
import config from "../common/Config";
import * as XLSX from "xlsx";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import jsPDF from "jspdf";
import "jspdf-autotable";
import '../common/commonStyles.css';
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const PaymentProcess = () => {
  const [reportrange, setReportrange] = useState("");
  const [dates, setDates] = useState([new Date(), new Date()]);
  const [draw] = useState(1);
  const [start] = useState(0);
  const [length] = useState(5000);
  const accessToken = localStorage.getItem("accessToken");

  const [receivedPayment, setReceivedPayment] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [shouldLoadPayments, setShouldLoadPayments] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerInputValue, setCustomerInputValue] = useState(null);
  const [salesrepOptions, setSalesrepOptions] = useState([]);
  const [salesrepInputValue, setSalesrepInputValue] = useState(null);
  const [shopDetails, setShopDetails] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);

  const table = useTable({
    defaultRowsPerPage: 50,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'date',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const totals = useMemo(() => {
    let total = 0;
    let totalCash = 0;
    let totalCheque = 0;
    let totalCard = 0;
    let totalBankTransfer = 0;
    let totalUPI = 0;

    filteredData.forEach((row) => {
      const amount = parseFloat(row.amount) || 0;
      total += amount;
      const mode = row.mode?.toLowerCase();
      if (mode === "cash") {
        totalCash += amount;
      } else if (mode === "cheque") {
        totalCheque += amount;
      } else if (mode === "card" || mode === "cc") {
        totalCard += amount;
      } else if (mode === "bank transfer") {
        totalBankTransfer += amount;
      } else if (mode === "upi") {
        totalUPI += amount;
      }
    });

    const formatAmt = (amt) => amt.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return {
      total,
      totalCash,
      totalCheque,
      totalCard,
      totalBankTransfer,
      totalUPI,
      formattedTotal: formatAmt(total),
      formattedCash: formatAmt(totalCash),
      formattedCard: formatAmt(totalCard),
      formattedCheque: formatAmt(totalCheque),
      formattedBankTransfer: formatAmt(totalBankTransfer),
      formattedUPI: formatAmt(totalUPI),
    };
  }, [filteredData]);

  const searchFormData = () => {
    const startDate = dates[0] ? format(dates[0], "dd/MM/yyyy") : "";
    const endDate = dates[1] ? format(dates[1], "dd/MM/yyyy") : "";
    setReportrange(startDate ? `${startDate} - ${endDate}` : "");
    setShouldLoadPayments(true);
  };

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    if (isInitialLoad) {
      loadReceivedPaymentList();
      setIsInitialLoad(false);
    } else if (shouldLoadPayments) {
      loadReceivedPaymentList();
      setShouldLoadPayments(false);
    }

    document.title = 'Payment Collection';
    return () => {
      document.title = 'Ebusso';
    };
  }, [shouldLoadPayments, accessToken, isInitialLoad]);

  const loadReceivedPaymentList = async () => {
    try {
      const customer = customerInputValue ? customerInputValue.value : "";
      const salesrep = salesrepInputValue ? salesrepInputValue.value : "";
      const data = await fetchReceivedPaymentcollectionList(
        draw,
        start,
        length,
        reportrange,
        accessToken,
        customer,
        salesrep
      );
      setReceivedPayment(data.data || []);
      setFilteredData(data.data || []);
      setShopDetails(data.shop_details || null);
      table.onChangePage(null, 0);
    } catch (error) {
      // Error handling
    }
  };

  const fetchCustomerNameOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerOptions([]);
      return;
    }

    const formData = new FormData();
    formData.append("term", inputValue);
    formData.append("limit", "10");

    try {
      const response = await fetch(`${config.API_URL}/suggestions`, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      const data = await response.json();
      if (Array.isArray(data.results)) {
        const formattedOptions = data.results.map((item) => ({
          label: item.text,
          value: item.id,
        }));
        setCustomerOptions(formattedOptions);
      }
    } catch (error) {
      setCustomerOptions([]);
    }
  };

  const exportToExcel = () => {
    const exportData = filteredData.map((item, i) => ({
      Sr: i + 1,
      Date: item.date || "",
      Customer: item.customer || "",
      Route: item.route || "",
      "Collected By": item.collected_by || "",
      Mode: item.mode || "",
      "Total Due": parseFloat(item.total_due) || "",
      Amount: parseFloat(item.amount) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, "PaymentCollection.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });
    const margin = 10;
    let y = 10;

    const logoUrl = `${config.BASE_URL}/assets/img/${accessToken}_logo.png`;

    let logoImg = logoBase64;
    if (!logoImg) {
      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        logoImg = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        setLogoBase64(logoImg);
      } catch {
        logoImg = null;
      }
    }

    const logoWidth = 40;
    const logoHeight = 18;
    if (logoImg) {
      try {
        doc.addImage(logoImg, 'PNG', margin, y, logoWidth, logoHeight);
      } catch (e) { }
    }

    if (shopDetails) {
      const detailsX = margin + logoWidth + 5;
      let detailsY = y + 2;

      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text(shopDetails.shop_name || '', detailsX, detailsY);
      detailsY += 6;

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      if (shopDetails.address) {
        doc.text(shopDetails.address.split(','), detailsX, detailsY);
        detailsY += 8;
      }

      let phoneEmail = [];
      if (shopDetails.phone) phoneEmail.push(`Phone No : ${shopDetails.phone}`);
      if (shopDetails.email) phoneEmail.push(`Email : ${shopDetails.email}`);
      if (phoneEmail.length > 0) {
        doc.text(phoneEmail.join(' | '), detailsX, detailsY);
        detailsY += 5;
      }
      let companyVat = [];
      if (shopDetails.company_reg_no) companyVat.push(`Company No : ${shopDetails.company_reg_no}`);
      if (shopDetails.vat_no) companyVat.push(`VAT No : ${shopDetails.vat_no}`);
      if (shopDetails.website) companyVat.push(`Website : ${shopDetails.website}`);
      if (companyVat.length > 0) {
        doc.text(companyVat.join(' | '), detailsX, detailsY);
        detailsY += 5;
      }

      y = Math.max(y + logoHeight + 2, detailsY);
    }

    doc.setFontSize(16);
    y += 5;

    let dateRangeText = "";
    if (dates && dates[0] && dates[1]) {
      dateRangeText = `Payment Collection Date : ${format(dates[0], "dd-MM-yyyy")} - ${format(dates[1], "dd-MM-yyyy")}`;
    }
    doc.setFontSize(12);
    doc.text(dateRangeText, margin, y);
    y += 6;

    const totalsText = [
      `Cash: ${totals.formattedCash}`,
      `Card: ${totals.formattedCard}`,
      `Cheque: ${totals.formattedCheque}`,
      `Bank Transfer: ${totals.formattedBankTransfer}`,
      `UPI: ${totals.formattedUPI}`,
      `Total Amount: ${totals.formattedTotal}`
    ].join("    ");
    doc.setFontSize(10);
    doc.text(totalsText, margin, y);
    y += 5;

    const tableColumn = [
      "Sr",
      "Date",
      "Customer",
      "Route",
      "Collected By",
      "Mode",
      "Total Due",
      "Amount",
    ];
    const tableRows = filteredData.map((item, i) => [
      i + 1,
      item.date || "",
      item.customer || "",
      item.route || "",
      item.collected_by || "",
      item.mode || "",
      parseFloat(item.total_due) || "0.00",
      parseFloat(item.amount) || 0,
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: y,
      styles: {
        fontSize: 8,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0]
      },
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.2,
      theme: 'grid',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      },
      columnStyles: {
        6: { halign: 'right' },
        7: { halign: 'right' }
      },
      margin: { left: 8, right: 8 }
    });

    doc.save("PaymentCollection.pdf");
  };

  const handleInputChange = (event, newInputValue) => {
    if (newInputValue && newInputValue.length >= 3) {
      fetchCustomerNameOptions(newInputValue);
    } else {
      setCustomerOptions([]);
    }
  };

  const handleChange = (event, newValue) => {
    setCustomerInputValue(newValue);
    setCustomerFilter(newValue?.label || "");
  };

  const handleDateChange = (value) => {
    setDates(value);
  };

  const handleSalesrepChange = (event, newValue) => {
    setSalesrepInputValue(newValue);
  };

  const fetchSalesreps = async () => {
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = {
        authorization: `Bearer ${accessToken}`,
      };
      let body = JSON.stringify({});
      if (useEncrypt) {
        const ep = encryptPayload({});
        if (ep) {
          const fd = new FormData();
          fd.append('encrypted_payload', ep);
          body = fd;
        } else {
          headers["Content-Type"] = "application/json";
        }
      } else {
        headers["Content-Type"] = "application/json";
      }
      const response = await fetch(`${config.API_URL}/getSalesRep`, {
        method: "POST",
        headers,
        body,
      });
      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      setSalesrepOptions(
        Array.isArray(data.data)
          ? data.data.map((rep) => ({
            value: rep.id,
            label: `${rep.first_name}${rep.last_name ? " " + rep.last_name : ""}`.trim(),
          }))
          : []
      );
    } catch (error) {
      setSalesrepOptions([]);
    }
  };

  useEffect(() => {
    fetchSalesreps();
  }, [accessToken]);

  useEffect(() => {
    let filtered = receivedPayment;
    if (customerFilter) {
      filtered = filtered.filter((item) =>
        item.customer?.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }
    if (salesrepInputValue?.label) {
      filtered = filtered.filter(
        (item) =>
          item.collected_by?.toLowerCase() ===
          salesrepInputValue.label.toLowerCase()
      );
    }
    setFilteredData(filtered);
    table.onChangePage(null, 0);
  }, [customerFilter, salesrepInputValue, receivedPayment]);

  const renderRow = (item, index) => {
    const { date, customer, route, collected_by, mode, total_due, amount } = item;

    return (
      <TableRow
        key={`${date}-${index}-${customer}`}
        className="table-cell-style"
      >
        <TableCell>{date || ''}</TableCell>
        <TableCell>{customer || ''}</TableCell>
        <TableCell>{route || ''}</TableCell>
        <TableCell>{collected_by || ''}</TableCell>
        <TableCell>{mode || ''}</TableCell>
        <TableCell align="right">
          {parseFloat(total_due || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </TableCell>
        <TableCell align="right">
          {parseFloat(amount || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
        <Box className="page-container">
          <Paper className="paper-style"
          >
            <Box sx={{ px: 2, py: 1.2 }}>
              <Grid2 container spacing={2} justifyContent="space-between" alignItems="center" mb={1}>
                <Grid2 size={{ xs: 12, md: 2}}>
                  <Typography className="page-title">
                    Payment Collection
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    showOneCalendar
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
                    style={{ width: "100%" }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 3 }}>
                  <Autocomplete
                    options={customerOptions}
                    value={customerInputValue}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        className="input-border"
                        {...params}
                        placeholder="Search..."
                        size="small"
                        label="Customer"
                        sx={{
                          backgroundColor: "#f5f7fa",
                          "&:hover": {
                            backgroundColor: "#fff"
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff"
                          },
                          '& .MuiFormLabel-root': {
                            top: '-2px'
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            top: '0px'
                          }
                        }}
                      />
                    )}
                    
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 3 }}>
                  <Autocomplete
                    options={salesrepOptions}
                    value={salesrepInputValue}
                    onChange={handleSalesrepChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        className="input-border"
                        {...params}
                        placeholder="Search..."
                        size="small"
                        label="Collected By"
                        sx={{
                          backgroundColor: "#f5f7fa",
                          "&:hover": {
                            backgroundColor: "#fff"
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff"
                          },
                          '& .MuiFormLabel-root': {
                            top: '-2px'
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            top: '0px'
                          }
                        }}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 1}}>
                  <Box sx={{ display: "flex", alignItems: "flex-end", height: "100%" }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={searchFormData}
                      className="button-variant-contained"
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid2>
              </Grid2>
           
              <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={{ xs: "column", md: "row" }} gap={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    Cash: <strong style={{ color: "#212b36" }}>{totals.formattedCash}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>|</Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    Card: <strong style={{ color: "#212b36" }}>{totals.formattedCard}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>|</Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    Cheque: <strong style={{ color: "#212b36" }}>{totals.formattedCheque}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>|</Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    Bank Transfer: <strong style={{ color: "#212b36" }}>{totals.formattedBankTransfer}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>|</Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    UPI: <strong style={{ color: "#212b36" }}>{totals.formattedUPI}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>|</Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    Total Amount: <strong style={{ color: "#212b36" }}>{totals.formattedTotal}</strong>
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
         
                    startIcon={<Iconify icon="eva:download-fill" width={16} />}
                    onClick={exportToExcel}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      px: 1.2,
                      py: 0.6,
                      borderColor: "#F8991D",
                      "&:hover": {
                        backgroundColor: "#F8991D",
                        color: "#fff",
                      },
                    }}
                  >
                    Export Excel
                  </Button>
                  <Button
                    variant="outlined"
        
                    startIcon={<Iconify icon="eva:file-text-fill" width={16} />}
                    onClick={exportToPDF}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      px: 1.2,
                      py: 0.6,
                      borderColor: "#F8991D",
                      "&:hover": {
                        backgroundColor: "#F8991D",
                        color: "#fff",
                      },
                    }}
                  >
                    Export PDF
                  </Button>
                </Stack>
              </Box>
              </Box>
              <TableContainer
              
              >
                <Table
                  size="small"
                  className="table-cell-border"
               
                >
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell width="120px" >
                        Date
                      </TableCell>
                      <TableCell width="400px" >
                        Customer Name(A/C No)
                      </TableCell>
                      <TableCell >
                        Route
                      </TableCell>
                      <TableCell >
                        Collected By
                      </TableCell>
                      <TableCell >
                        Mode
                      </TableCell>
                      <TableCell align="right" >
                        Amount Due
                      </TableCell>
                      <TableCell align="right" >
                        Collected Amt
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#637381",
                              fontSize: "0.75rem",
                              lineHeight: 1.5,
                            }}
                          >
                            No payment collection data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData
                        .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                        .map((item, index) => renderRow(item, index))
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5}></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#212b36" }}>
                        Total:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#212b36" }}>
                        {totals.formattedTotal}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>

              <Box sx={{ px: 2, pt: 2 }}>
                <TablePaginationCustom
                  page={table.page}
                  dense={table.dense}
                  count={filteredData.length}
                  rowsPerPage={table.rowsPerPage}
                  onPageChange={table.onChangePage}
                  onRowsPerPageChange={table.onChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Box>
          
          </Paper>
        </Box>
    </>
  );
};

export default PaymentProcess;
